import { FC } from 'react';

import { VIEW_DATE_FORMAT } from 'utils/constants/date';
import { formatDate } from 'utils/date';

import { ActionBar, BottomSheet, Button, HSpacingContainer, Modal, NavBar, Text, VSpacing } from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import useTrls from 'hooks/translation/useTrls';

import { UsersTagList } from 'components/AccountCreate/components/UsersTagList';
import { createAccount } from 'components/AccountCreate/createAccount';
import { MagritteModalHeader } from 'components/common/MagritteModalHeader/MagritteModalHeader';

import { useDispatch, useSelector } from 'store/utils';

interface ConfirmationModalProps {
    isVisible: boolean;
    setVisible: (isVisible: boolean) => void;
}

const TrlKeys = {
    title: 'account.create.title',
    accountTitle: {
        label: 'AccountCreate.ConfirmationModal.accountTitle.label',
    },
    users: {
        label: 'account.create.confirmation.users',
    },
    pricePerClick: {
        label: 'AccountCreate.ConfirmationModal.pricePerClick.label',
        text: 'AccountCreate.ConfirmationModal.pricePerClick.text',
    },
    expiredDate: {
        label: 'AccountCreate.ConfirmationModal.expiredDate.label',
        text: 'AccountCreate.ConfirmationModal.expiredDate.text',
    },
    cancel: 'account.create.confirmation.back',
    confirmButton: 'account.create.confirmation.create',
};

export const ConfirmationModal: FC<ConfirmationModalProps> = ({ isVisible, setVisible }) => {
    const { isContractSettings, accountTitle, pricePerClick, expiredDate } = useSelector((state) => state.accounts);

    const dispatch = useDispatch();

    const trl = useTrls();

    const content = (
        <>
            <Text typography="subtitle-1-semibold">{trl(TrlKeys.accountTitle.label)}</Text>
            <VSpacing default={8} />
            <Text typography="paragraph-2-regular">{accountTitle}</Text>
            <VSpacing default={24} />
            <Text typography="subtitle-1-semibold">{trl(TrlKeys.users.label)}</Text>
            <VSpacing default={8} />
            <UsersTagList needEmptyText />
            {isContractSettings && (
                <>
                    <VSpacing default={24} />
                    <Text typography="subtitle-1-semibold">{trl(TrlKeys.pricePerClick.label)}</Text>
                    <VSpacing default={8} />
                    <Text typography="paragraph-2-regular">{trl(TrlKeys.pricePerClick.text, [pricePerClick])}</Text>
                    <VSpacing default={24} />
                    <Text typography="subtitle-1-semibold">{trl(TrlKeys.expiredDate.label)}</Text>
                    <VSpacing default={8} />
                    <Text typography="paragraph-2-regular">
                        {trl(TrlKeys.expiredDate.text, [formatDate(expiredDate, VIEW_DATE_FORMAT)])}
                    </Text>
                </>
            )}
            <VSpacing default={8} />
        </>
    );

    const footer = (
        <ActionBar
            secondaryActions={
                <Button mode="secondary" style="neutral" onClick={() => setVisible(false)}>
                    {trl(TrlKeys.cancel)}
                </Button>
            }
            primaryActions={
                <Button
                    mode="primary"
                    style="accent"
                    onClick={() => dispatch(createAccount(setVisible, false))}
                    data-qa="confirmation-create"
                >
                    {trl(TrlKeys.confirmButton)}
                </Button>
            }
        />
    );

    return (
        <>
            <Modal
                size="medium"
                visible={isVisible}
                onClose={() => setVisible(false)}
                header={
                    <MagritteModalHeader
                        title={trl(TrlKeys.title)}
                        actions={<CrossOutlinedSize24 initialColor="secondary" onClick={() => setVisible(false)} />}
                    />
                }
                footer={footer}
            >
                {content}
            </Modal>
            <BottomSheet
                height="content"
                visible={isVisible}
                onClose={() => setVisible(false)}
                header={
                    <HSpacingContainer default={12}>
                        <NavBar>
                            <NavBar.Pane>
                                <Text typography="title-4-semibold">{trl(TrlKeys.title)}</Text>
                            </NavBar.Pane>
                        </NavBar>
                    </HSpacingContainer>
                }
                footer={footer}
            >
                {content}
            </BottomSheet>
        </>
    );
};
